/**
 * Projects Page
 *
 * Showcase of community projects with voting and donation features.
 * Projects are decentralized applications built on Flux ecosystem.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useToast } from '../components/Toast';
import { StorageService } from '../services/storage';

// Project status types
type ProjectStatus = 'development' | 'beta' | 'live' | 'planned';

// Project data interface (non-translatable fields)
interface ProjectData {
  id: string;
  translationKey: string; // Key for i18n translations
  status: ProjectStatus;
  category: string;
  image?: string;
  technologies: string[];
  partnerships: string[];
  links: {
    website?: string;
    github?: string;
    docs?: string;
    demo?: string;
  };
  donations: {
    flux?: string;
    btc?: string;
    eth?: string;
  };
  launchDate?: string;
  progress: number; // 0-100
}

// Project interface with translated content
interface Project extends ProjectData {
  name: string;
  tagline: string;
  description: string;
  longDescription: string;
  features: string[];
}

// Projects data (technical/non-translatable)
const PROJECTS_DATA: ProjectData[] = [
  {
    id: 'getspecter-app',
    translationKey: 'getspecter',
    status: 'development',
    category: 'Communication',
    image: '/GetSpecter.app.gif',
    technologies: ['React Native', 'Tauri', 'Signal Protocol', 'Kyber-1024', 'X25519', 'Flux', 'Turborepo', 'TypeScript', 'WebRTC'],
    partnerships: ['RunOnFlux', 'Signal Foundation (Protocol)'],
    links: {
      website: 'https://getspecter.app',
      github: 'https://github.com/edwqdq-tech/getspecter',
      docs: 'https://docs.getspecter.app',
    },
    donations: {
      flux: 't1GetSpecterDonationAddress',
      btc: 'bc1qGetSpecterBTCAddress',
      eth: '0xGetSpecterETHAddress',
    },
    launchDate: '2026-Q2',
    progress: 70,
  },
  {
    id: 'edouard-v2',
    translationKey: 'edouard-v2',
    status: 'development',
    category: 'Finance',
    image: '/EdouardAI-V2.svg',
    technologies: ['React', 'React Native', 'Supabase', 'PostgreSQL', 'TypeScript', 'Vercel'],
    partnerships: ['Supabase', 'Vercel', 'CoinGecko'],
    links: {
      website: 'https://edouard.ai',
      docs: 'https://docs.edouard.ai',
    },
    donations: {
      flux: 't1EdouardAIDonationFlux',
      btc: 'bc1qEdouardAIDonationBTC',
      eth: '0xEdouardAIDonationETH',
    },
    launchDate: '2026-Q2',
    progress: 45,
  },
  {
    id: 'fluxdvpn',
    translationKey: 'fluxdvpn',
    status: 'planned',
    category: 'Privacy',
    image: '/FluxDVPN.svg',
    technologies: ['Swift', 'SwiftUI', 'Go', 'WireGuard', 'Docker', 'IPFS', 'Flux'],
    partnerships: ['RunOnFlux', 'WireGuard'],
    links: {
      website: 'https://fluxdvpn.io',
      github: 'https://github.com/edwqdq-tech/fluxdvpn',
      docs: 'https://docs.fluxdvpn.io',
    },
    donations: {
      flux: 't1FluxDVPNDonationFlux',
      btc: 'bc1qFluxDVPNDonationBTC',
      eth: '0xFluxDVPNDonationETH',
    },
    launchDate: '2026-Q3',
    progress: 38,
  },
  {
    id: 'masternodes-online',
    translationKey: 'masternodes',
    status: 'development',
    category: 'Analytics',
    image: '/MasterNodes.svg',
    technologies: ['Next.js', 'TypeScript', 'Supabase', 'PostgreSQL', 'TailwindCSS', 'Vercel'],
    partnerships: ['Supabase', 'Vercel', 'CoinGecko'],
    links: {
      website: 'https://masternodes.online',
      github: 'https://github.com/edwqdq-tech/masternodes-online',
    },
    donations: {
      flux: 't1MasterNodesOnlineDonationFlux',
      btc: 'bc1qMasterNodesOnlineDonationBTC',
      eth: '0xMasterNodesOnlineDonationETH',
    },
    launchDate: '2026-Q2',
    progress: 35,
  },
];

// Helper function to get translated projects
function useTranslatedProjects(): Project[] {
  const { t } = useTranslation();

  return PROJECTS_DATA.map(projectData => ({
    ...projectData,
    name: t(`projects.list.${projectData.translationKey}.name`),
    tagline: t(`projects.list.${projectData.translationKey}.tagline`),
    description: t(`projects.list.${projectData.translationKey}.description`),
    longDescription: t(`projects.list.${projectData.translationKey}.longDescription`),
    features: t(`projects.list.${projectData.translationKey}.features`, { returnObjects: true }) as string[],
  }));
}

// Storage key for votes
const VOTES_STORAGE_KEY = 'edouard_project_votes';

// Status badge component
function StatusBadge({ status }: { status: ProjectStatus }) {
  const { t } = useTranslation();

  const config = {
    development: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', label: t('projects.status.development', 'En développement') },
    beta: { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.15)', label: t('projects.status.beta', 'Beta') },
    live: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', label: t('projects.status.live', 'En ligne') },
    planned: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)', label: t('projects.status.planned', 'Planifié') },
  };

  const { color, bg, label } = config[status];

  return (
    <span style={{
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: 600,
      color,
      background: bg,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}>
      {label}
    </span>
  );
}

// Progress bar component
function ProgressBar({ progress }: { progress: number }) {
  return (
    <div style={{
      width: '100%',
      height: '6px',
      background: 'var(--border)',
      borderRadius: '3px',
      overflow: 'hidden',
    }}>
      <div style={{
        width: `${progress}%`,
        height: '100%',
        background: progress < 30 ? '#ef4444' : progress < 70 ? '#f59e0b' : '#10b981',
        borderRadius: '3px',
        transition: 'width 0.3s ease',
      }} />
    </div>
  );
}

// Project card component
function ProjectCard({
  project,
  votes,
  hasVoted,
  onVote,
  onViewDetails,
  onDonate
}: {
  project: Project;
  votes: number;
  hasVoted: boolean;
  onVote: () => void;
  onViewDetails: () => void;
  onDonate: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div style={{
      background: 'var(--card)',
      borderRadius: '16px',
      border: '1px solid var(--border)',
      overflow: 'hidden',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'pointer',
    }}
    onClick={onViewDetails}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    >
      {/* Header with image or gradient */}
      <div style={{
        height: '160px',
        background: project.image ? 'var(--bg-secondary)' : `linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {project.image ? (
          <img
            src={project.image}
            alt={project.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <div style={{
            width: '60px',
            height: '60px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icons.Rocket size={32} style={{ color: 'white' }} />
          </div>
        )}
        <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
          <StatusBadge status={project.status} />
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '20px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>
          {project.name}
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--accent)', marginBottom: '12px' }}>
          {project.tagline}
        </p>
        <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px', lineHeight: 1.5 }}>
          {project.description}
        </p>

        {/* Technologies */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
          {project.technologies.slice(0, 4).map(tech => (
            <span key={tech} style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              background: 'var(--hover)',
              color: 'var(--text)',
            }}>
              {tech}
            </span>
          ))}
        </div>

        {/* Progress */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('projects.progress', 'Progression')}
            </span>
            <span style={{ fontSize: '12px', fontWeight: 600 }}>{project.progress}%</span>
          </div>
          <ProgressBar progress={project.progress} />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onVote}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '8px',
              border: hasVoted ? 'none' : '1px solid var(--border)',
              background: hasVoted ? 'var(--accent)' : 'transparent',
              color: hasVoted ? 'white' : 'var(--text)',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s',
            }}
          >
            <Icons.ThumbsUp size={16} />
            {votes} {t('projects.votes', 'votes')}
          </button>
          <button
            onClick={onDonate}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <Icons.Heart size={16} />
            {t('projects.donate', 'Soutenir')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Donation modal component
function DonationModal({
  project,
  isOpen,
  onClose
}: {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  if (!isOpen || !project) return null;

  const copyAddress = (address: string, crypto: string) => {
    navigator.clipboard.writeText(address);
    toast(t('projects.addressCopied', 'Adresse {{crypto}} copiée !', { crypto }), 'success');
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
        }}
        onClick={onClose}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'var(--card)',
        borderRadius: '16px',
        padding: '24px',
        width: '90%',
        maxWidth: '450px',
        zIndex: 10000,
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.Heart size={24} style={{ color: '#10b981' }} />
            {t('projects.supportProject', 'Soutenir')} {project.name}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)' }}>
            <Icons.X size={20} />
          </button>
        </div>

        <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '20px' }}>
          {t('projects.donationDescription', 'Vos dons permettent de financer le développement et l\'hébergement de ce projet open source.')}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {project.donations.flux && (
            <div style={{
              padding: '16px',
              background: 'var(--hover)',
              borderRadius: '12px',
              border: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '12px',
                }}>
                  FLUX
                </div>
                <span style={{ fontWeight: 600 }}>Flux</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                background: 'var(--bg-secondary)',
                borderRadius: '8px',
                fontSize: '12px',
                fontFamily: 'monospace',
              }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {project.donations.flux}
                </span>
                <button
                  onClick={() => copyAddress(project.donations.flux!, 'FLUX')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: 'var(--accent)',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Icons.Copy size={14} />
                </button>
              </div>
            </div>
          )}

          {project.donations.btc && (
            <div style={{
              padding: '16px',
              background: 'var(--hover)',
              borderRadius: '12px',
              border: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '12px',
                }}>
                  BTC
                </div>
                <span style={{ fontWeight: 600 }}>Bitcoin</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                background: 'var(--bg-secondary)',
                borderRadius: '8px',
                fontSize: '12px',
                fontFamily: 'monospace',
              }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {project.donations.btc}
                </span>
                <button
                  onClick={() => copyAddress(project.donations.btc!, 'BTC')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: 'var(--accent)',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Icons.Copy size={14} />
                </button>
              </div>
            </div>
          )}

          {project.donations.eth && (
            <div style={{
              padding: '16px',
              background: 'var(--hover)',
              borderRadius: '12px',
              border: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '12px',
                }}>
                  ETH
                </div>
                <span style={{ fontWeight: 600 }}>Ethereum</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                background: 'var(--bg-secondary)',
                borderRadius: '8px',
                fontSize: '12px',
                fontFamily: 'monospace',
              }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {project.donations.eth}
                </span>
                <button
                  onClick={() => copyAddress(project.donations.eth!, 'ETH')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: 'var(--accent)',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Icons.Copy size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Project details modal
function ProjectDetailsModal({
  project,
  isOpen,
  onClose,
  votes,
  hasVoted,
  onVote,
  onDonate,
}: {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  votes: number;
  hasVoted: boolean;
  onVote: () => void;
  onDonate: () => void;
}) {
  const { t } = useTranslation();

  if (!isOpen || !project) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
        }}
        onClick={onClose}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'var(--card)',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '85vh',
        overflow: 'hidden',
        zIndex: 10000,
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Hero section with image background or gradient */}
        <div style={{
          position: 'relative',
          minHeight: '230px',
          overflow: 'hidden',
        }}>
          {/* Background image */}
          {project.image && (
            <img
              src={project.image}
              alt={project.name}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}

          {/* Gradient overlay for readability when image exists */}
          {project.image && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 100%)',
            }} />
          )}

          {/* Content container */}
          <div style={{
            position: 'relative',
            padding: '24px',
            background: project.image ? 'transparent' : 'linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%)',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            minHeight: '182px',
          }}>
            {/* Close button */}
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0,0,0,0.5)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                color: 'white',
              }}
            >
              <Icons.X size={20} />
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h2 style={{ fontSize: '24px', fontWeight: 700, textShadow: project.image ? '0 2px 4px rgba(0,0,0,0.5)' : 'none' }}>{project.name}</h2>
                  <StatusBadge status={project.status} />
                </div>
                <p style={{ fontSize: '16px', opacity: 0.9, textShadow: project.image ? '0 1px 3px rgba(0,0,0,0.5)' : 'none' }}>{project.tagline}</p>
              </div>
            </div>

            {/* Progress */}
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', opacity: 0.9 }}>{t('projects.progress', 'Progression')}</span>
                <span style={{ fontSize: '13px', fontWeight: 600 }}>{project.progress}%</span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                background: 'rgba(255,255,255,0.3)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${project.progress}%`,
                  height: '100%',
                  background: 'white',
                  borderRadius: '4px',
                }} />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {/* Description */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--muted)' }}>
              {t('projects.about', 'A propos')}
            </h3>
            <div style={{
              fontSize: '14px',
              lineHeight: 1.7,
              color: 'var(--text)',
              whiteSpace: 'pre-line',
            }}>
              {project.longDescription}
            </div>
          </div>

          {/* Features */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--muted)' }}>
              {t('projects.features', 'Fonctionnalités')}
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {project.features.map(feature => (
                <span key={feature} style={{
                  padding: '8px 14px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  background: 'var(--hover)',
                  color: 'var(--text)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  <Icons.Check size={14} style={{ color: '#10b981' }} />
                  {feature}
                </span>
              ))}
            </div>
          </div>

          {/* Technologies */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--muted)' }}>
              {t('projects.technologies', 'Technologies')}
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {project.technologies.map(tech => (
                <span key={tech} style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  background: 'var(--accent)',
                  color: 'white',
                  fontWeight: 500,
                }}>
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Partnerships */}
          {project.partnerships.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--muted)' }}>
                {t('projects.partnerships', 'Partenariats')}
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {project.partnerships.map(partner => (
                  <span key={partner} style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontWeight: 500,
                  }}>
                    {partner}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Links */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--muted)' }}>
              {t('projects.links', 'Liens')}
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {project.links.website && (
                <a
                  href={project.links.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    background: 'var(--hover)',
                    color: 'var(--text)',
                    textDecoration: 'none',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Icons.Globe size={16} />
                  Website
                </a>
              )}
              {project.links.github && (
                <a
                  href={project.links.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    background: 'var(--hover)',
                    color: 'var(--text)',
                    textDecoration: 'none',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Icons.Github size={16} />
                  GitHub
                </a>
              )}
              {project.links.docs && (
                <a
                  href={project.links.docs}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    background: 'var(--hover)',
                    color: 'var(--text)',
                    textDecoration: 'none',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Icons.FileText size={16} />
                  Documentation
                </a>
              )}
            </div>
          </div>

          {/* Launch date */}
          {project.launchDate && (
            <div style={{
              padding: '16px',
              background: 'var(--hover)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <Icons.Calendar size={20} style={{ color: 'var(--accent)' }} />
              <div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  {t('projects.expectedLaunch', 'Lancement prévu')}
                </div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{project.launchDate}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: '12px',
        }}>
          <button
            onClick={onVote}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '10px',
              border: hasVoted ? 'none' : '1px solid var(--border)',
              background: hasVoted ? 'var(--accent)' : 'transparent',
              color: hasVoted ? 'white' : 'var(--text)',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Icons.ThumbsUp size={18} />
            {hasVoted ? t('projects.voted', 'Voté !') : t('projects.vote', 'Voter')} ({votes})
          </button>
          <button
            onClick={onDonate}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Icons.Heart size={18} />
            {t('projects.donate', 'Soutenir')}
          </button>
        </div>
      </div>
    </>
  );
}

// Main Projects page component
export function Projects() {
  const { t } = useTranslation();
  const toast = useToast();
  const projects = useTranslatedProjects();

  const [votes, setVotes] = useState<Record<string, number>>({});
  const [userVotes, setUserVotes] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDonation, setShowDonation] = useState(false);
  const [filter, setFilter] = useState<ProjectStatus | 'all'>('all');

  // Load votes from storage
  useEffect(() => {
    const savedVotes = StorageService.get<Record<string, number>>(VOTES_STORAGE_KEY, {});
    const savedUserVotes = StorageService.get<string[]>(`${VOTES_STORAGE_KEY}_user`, []);
    setVotes(savedVotes);
    setUserVotes(savedUserVotes);
  }, []);

  // Track page view on mount
  useEffect(() => {
  }, []);

  // Handle vote
  const handleVote = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    const projectName = project?.name || projectId;

    if (userVotes.includes(projectId)) {
      // Remove vote
      const newUserVotes = userVotes.filter(id => id !== projectId);
      const newVotes = { ...votes, [projectId]: (votes[projectId] || 1) - 1 };
      setUserVotes(newUserVotes);
      setVotes(newVotes);
      StorageService.set(`${VOTES_STORAGE_KEY}_user`, newUserVotes);
      StorageService.set(VOTES_STORAGE_KEY, newVotes);
      toast(t('projects.voteRemoved', 'Vote retiré'), 'info');
    } else {
      // Add vote
      const newUserVotes = [...userVotes, projectId];
      const newVotes = { ...votes, [projectId]: (votes[projectId] || 0) + 1 };
      setUserVotes(newUserVotes);
      setVotes(newVotes);
      StorageService.set(`${VOTES_STORAGE_KEY}_user`, newUserVotes);
      StorageService.set(VOTES_STORAGE_KEY, newVotes);
      toast(t('projects.voteAdded', 'Vote ajouté ! Merci pour votre soutien'), 'success');
    }
  };

  // Filter projects
  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(p => p.status === filter);

  return (
    <div style={{ paddingBottom: '40px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Icons.Rocket size={32} style={{ color: 'var(--accent)' }} />
          {t('projects.title', 'Projets Communautaires')}
        </h1>
        <p style={{ fontSize: '15px', color: 'var(--muted)', maxWidth: '600px' }}>
          {t('projects.description', 'Découvrez les projets en développement, votez pour vos favoris et soutenez l\'écosystème décentralisé.')}
        </p>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        flexWrap: 'wrap',
      }}>
        {(['all', 'live', 'beta', 'development', 'planned'] as const).map(status => (
          <button
            key={status}
            onClick={() => {
              setFilter(status);
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: filter === status ? 'none' : '1px solid var(--border)',
              background: filter === status ? 'var(--accent)' : 'transparent',
              color: filter === status ? 'white' : 'var(--text)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {status === 'all' ? t('projects.filter.all', 'Tous') :
             status === 'live' ? t('projects.status.live', 'En ligne') :
             status === 'beta' ? t('projects.status.beta', 'Beta') :
             status === 'development' ? t('projects.status.development', 'En dev') :
             t('projects.status.planned', 'Planifié')}
          </button>
        ))}
      </div>

      {/* Projects grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '24px',
      }}>
        {filteredProjects.map(project => (
          <ProjectCard
            key={project.id}
            project={project}
            votes={votes[project.id] || 0}
            hasVoted={userVotes.includes(project.id)}
            onVote={() => handleVote(project.id)}
            onViewDetails={() => {
              setSelectedProject(project);
              setShowDetails(true);
            }}
            onDonate={() => {
              setSelectedProject(project);
              setShowDonation(true);
            }}
          />
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--muted)',
        }}>
          <Icons.Inbox size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>{t('projects.noProjects', 'Aucun projet trouvé avec ce filtre')}</p>
        </div>
      )}

      {/* Project details modal */}
      <ProjectDetailsModal
        project={selectedProject}
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        votes={selectedProject ? (votes[selectedProject.id] || 0) : 0}
        hasVoted={selectedProject ? userVotes.includes(selectedProject.id) : false}
        onVote={() => selectedProject && handleVote(selectedProject.id)}
        onDonate={() => {
          setShowDetails(false);
          setShowDonation(true);
          if (selectedProject) {
          }
        }}
      />

      {/* Donation modal */}
      <DonationModal
        project={selectedProject}
        isOpen={showDonation}
        onClose={() => setShowDonation(false)}
      />
    </div>
  );
}
